/**
 * MONSTER TOWN — BUILD GESTURES. OWNED BY THE BUILD CREW (src/build/**).
 *
 * The four things a finger can do in build mode, and nothing else:
 *
 *   TAP ground        -> the piece lands there (on top of whatever is already
 *                        in that column, so tapping a block's top stacks).
 *   PRESS AND REST    -> exactly the same thing. See below.
 *   DRAG              -> the ghost slides under the finger; lifting off places.
 *                        Starting the drag ON a placed piece slides THAT piece.
 *   TAP-HOLD a piece  -> pick it up; it follows until you tap it down again.
 *   TAP a piece (with the trash tool on) -> it goes away. Undo puts it back.
 *
 * ============ A 3-YEAR-OLD DOES NOT TAP: THEY PRESS AND REST ============
 * Measured on the same patch of open ground, iPad, real touch events: a quick
 * tap emitted `input:tap` -> `build:placed` and a piece appeared. Pressing and
 * resting for 900 ms emitted `input:hold` and NOTHING ELSE — no piece, no
 * sound, no fizzle — because this file returned immediately unless a hold had
 * landed on an existing instance. `src/input/touch.ts` swallows the tap on
 * release once a hold has fired, so the gesture died here.
 *
 * The same gesture already worked in PLAY mode (`installRestingFinger()` in
 * src/ui/touch.ts re-emits it), and it was left broken in BUILD. That is the
 * closest thing in the game to an age mode: a five-year-old who taps crisply
 * builds a town and their three-year-old brother, who presses and rests, cannot
 * build at all (LAW #3). So a hold on OPEN GROUND is now a tap: same landing,
 * same sound, same undo entry.
 *
 * A hold ON A PIECE still means "pick this up" — that gesture is not ambiguous,
 * a kid pressing on a chair is reaching for the chair.
 * =======================================================================
 *
 * DELIBERATE OMISSIONS: there is no double-tap, no pixel-accurate dragging, no
 * modifier, and no destructive gesture that can fire by accident — a piece is
 * only ever removed in an explicit, visibly-highlighted trash mode.
 *
 * Nothing here adds a pointer listener: every gesture arrives through the one
 * dispatcher in src/app/router.ts as an `input:*` event.
 */

import { bus, type ResolvedInput } from '../core/events'
import { world } from '../core/scene'
import { pickInstance } from '../input/pick'
import type { InstanceId, SceneId } from '../core/types'
import { app, type GameContext } from '../app/context'
import { refreshGhost, setGhostPoint, updateGhostAt } from './ghost'
import { landingFor, moveInstance, placeAtPoint, removeInstance, undo } from './placer'
import { buildTool, carried, rotateSelection, selectedPlaceable, selectionRot, setCarry } from './state'

// ---------------------------------------------------------------------------
// carry — pick a placed piece up and slide it
// ---------------------------------------------------------------------------

function artOf(sceneId: SceneId, id: InstanceId): { visible: boolean } | undefined {
  return world.get(sceneId)?.art.get(id)
}

/**
 * ============ A PICKUP MUST BE ON THE PIECE, NOT NEAR IT (fixed r3) ============
 * Round-3 critic, major: "a 700ms stationary press on that block emitted
 * input:hold + build:carryStart; the very next tap elsewhere on the ground moved
 * its anchor … a kid poking at the house they just built will silently drag its
 * walls across the town."
 *
 * Part of why that fires so easily is `pickInstance`'s deliberate 44 px slop:
 * it is right for PLAY (a 3-year-old aims at a chair and hits the floor, and the
 * chair should still giggle) and wrong for a destructive-feeling move, where a
 * finger resting on empty grass NEXT TO a wall was enough to lift the wall.
 *
 * So a pickup re-picks with ZERO slop: rest your finger on the piece and you
 * pick it up, rest it on the ground beside the piece and you place there, which
 * is what a kid meant either way. The forgiving pick is untouched everywhere
 * else. (The rest of the answer is visible state: the piece stays standing where
 * it was, a teal marker holds its old tile, the whole drawer turns teal and
 * grows a PUT IT BACK button, and the move is one undo away.)
 * ==============================================================================
 */
let ctxRef: GameContext | null = null

function exactInstanceAt(e: ResolvedInput): InstanceId | null {
  if (e.instanceId === null) return null
  const ctx = ctxRef
  if (ctx === null) return e.instanceId
  const node = world.get(e.sceneId)
  if (node === undefined) return null
  const hit = pickInstance(ctx.camera.camera, e.point, node, ctx.canvas, 0)
  return hit === null ? null : hit.instanceId
}

export function beginCarry(sceneId: SceneId, instanceId: InstanceId): boolean {
  if (carried() !== null) return false
  const node = world.get(sceneId)
  if (node === undefined) return false
  const p = node.grid.placement(instanceId)
  if (p === null) return false

  setCarry({ instanceId, defId: p.defId, sceneId, from: p.anchor, fromRot: p.rot })
  /*
   * THE PIECE STAYS STANDING WHERE IT WAS. It used to be hidden the instant a
   * finger rested on it, so an accidental pickup read as "the wall I just built
   * vanished" — the worst thing that can happen to a 3-year-old in a building
   * game. It now stays put until the kid actually puts it somewhere, and the
   * ghost shows where that would be.
   */
  const art = artOf(sceneId, instanceId)
  if (art !== undefined) art.visible = true
  /*
   * AIM THE PREVIEW AT THE PIECE'S OWN SQUARE.
   *
   * A finger resting on a block is pointing, in ray terms, at the GROUND BEHIND
   * the block — often a tile something else is standing on — so the first frame
   * of a pickup showed a red "won't fit" pad one square up-screen from the thing
   * the kid had just touched. Starting on its own square is always valid (the
   * landing test ignores the piece being carried) and says the true thing: this
   * is where it is, move your finger to move it.
   */
  const home = node.grid.cellToWorld({ ...p.anchor, gy: 0 })
  setGhostPoint({ x: home.x, z: home.z })
  bus.emit('build:carryStart', { instanceId, defId: p.defId, from: p.anchor })
  bus.emit('audio:cue', { cue: 'pick_up' })
  bus.emit('ui:toast', { icon: 'tap', ms: 1400 })
  refreshGhost(sceneId)
  return true
}

/** Put the carried piece down at a world point. Never fails destructively. */
export function dropCarry(groundX: number | null, groundZ: number | null): void {
  const carry = carried()
  if (carry === null) return
  const art = artOf(carry.sceneId, carry.instanceId)
  if (art !== undefined) art.visible = true
  setCarry(null)

  let placed = false
  let at = carry.from
  const node = world.get(carry.sceneId)
  if (node !== undefined && groundX !== null && groundZ !== null) {
    const rot = selectionRot()
    // Exactly the landing the ghost was showing — what you saw is what you get.
    const landing = landingFor(node, carry.defId, groundX, groundZ, rot, carry.instanceId)
    if (landing.valid && landing.swaps === null) {
      placed = moveInstance(carry.sceneId, carry.instanceId, landing.anchor, rot)
      if (placed) at = landing.anchor
    }
  }
  bus.emit('build:carryEnd', { instanceId: carry.instanceId, placed, at })
  refreshGhost(carry.sceneId)
}

/** Any interruption puts the piece back exactly where it was. Nothing is lost. */
export function cancelCarry(): void {
  const carry = carried()
  if (carry === null) return
  const art = artOf(carry.sceneId, carry.instanceId)
  if (art !== undefined) art.visible = true
  setCarry(null)
  bus.emit('build:carryEnd', { instanceId: carry.instanceId, placed: false, at: carry.from })
}

// ---------------------------------------------------------------------------
// install
// ---------------------------------------------------------------------------

export function installGestures(ctx: GameContext): void {
  ctxRef = ctx
  /**
   * A hold that already put a piece down. The touch layer keeps sending drag
   * events after a hold fires, so without this a kid who presses, rests, then
   * slides their finger a centimetre would place once on the hold and then get
   * a second attempt on lift — landing on the tile they just filled, which
   * fizzles as a refusal. One gesture, one piece.
   */
  let placedOnHold = false

  /** The tap rule, shared by a quick tap and by a resting finger. */
  const buildTap = (e: ResolvedInput): boolean => {
    if (carried() !== null) {
      dropCarry(e.ground?.x ?? null, e.ground?.z ?? null)
      return true
    }

    if (buildTool() === 'delete') {
      if (e.instanceId !== null) removeInstance(e.sceneId, e.instanceId)
      return true
    }

    const defId = selectedPlaceable()
    if (defId === null || e.ground === null) return false
    setGhostPoint({ x: e.ground.x, z: e.ground.z })
    placeAtPoint(e.sceneId, defId, e.ground.x, e.ground.z, selectionRot())
    refreshGhost(e.sceneId)
    return true
  }

  bus.on('input:tap', (e) => {
    if (e.mode !== 'build') return
    placedOnHold = false
    buildTap(e)
  })

  // Nothing destructive happens on press — only on release, and undo is one
  // tap away. Hold lifts a piece; it is never deleted by a long press.
  bus.on('input:hold', (e) => {
    if (e.mode !== 'build') return
    placedOnHold = false

    // A carried piece comes down wherever the finger is resting.
    if (carried() !== null) {
      dropCarry(e.ground?.x ?? null, e.ground?.z ?? null)
      return
    }

    if (e.instanceId !== null) {
      if (buildTool() === 'delete') {
        removeInstance(e.sceneId, e.instanceId)
        return
      }
      // ON the piece, not merely near it. See `exactInstanceAt` above.
      const exact = exactInstanceAt(e)
      if (exact !== null) {
        // `beginCarry` aims the preview at the piece's own square.
        beginCarry(e.sceneId, exact)
        return
      }
    }

    // OPEN GROUND: press-and-rest IS a tap (see the header).
    placedOnHold = buildTap(e)
  })

  bus.on('input:dragStart', (e) => {
    if (e.mode !== 'build') return
    if (e.ground !== null) setGhostPoint({ x: e.ground.x, z: e.ground.z })
    if (buildTool() === 'delete') return
    if (carried() === null && e.instanceId !== null) {
      beginCarry(e.sceneId, e.instanceId)
      return
    }
    refreshGhost(e.sceneId)
  })

  bus.on('input:dragMove', (e) => {
    if (e.mode !== 'build' || e.ground === null) return
    if (buildTool() === 'delete') return
    updateGhostAt(e.sceneId, e.ground.x, e.ground.z)
  })

  bus.on('input:dragEnd', (e) => {
    if (e.mode !== 'build') return
    if (carried() !== null) {
      dropCarry(e.ground?.x ?? null, e.ground?.z ?? null)
      return
    }
    // This gesture already delivered its piece on the hold. One press, one
    // piece — never a second attempt onto the tile it just filled.
    if (placedOnHold) {
      placedOnHold = false
      refreshGhost(e.sceneId)
      return
    }
    if (buildTool() === 'delete') return
    const defId = selectedPlaceable()
    if (defId === null || e.ground === null) return
    placeAtPoint(e.sceneId, defId, e.ground.x, e.ground.z, selectionRot())
    refreshGhost(e.sceneId)
  })

  /**
   * A REFUSED TAP MUST STILL DO SOMETHING.
   *
   * `build:rejected` had no listeners anywhere in the game. The ghost does turn
   * red on the tapped tile, which is the right visual — but on an iPad there is
   * no hover, so the very first time a kid sees the red ghost is the frame their
   * finger lifts, and nothing else happens at all: no sound, no movement, no
   * hint. Tap a corner that is already full five times and the game is
   * completely silent. That reads as "broken", not as "won't fit".
   *
   * So: a soft, friendly "not there" — never a buzzer, never a punishment
   * (LAW #2). The red ring is already on the tile they aimed at; this just makes
   * sure the refusal is something they can HEAR and SEE happen.
   */
  bus.on('build:rejected', (e) => {
    bus.emit('audio:cue', { cue: 'build_reject', volume: 0.5 })
    const node = world.get(world.activeSceneId ?? ('' as SceneId))
    if (node === undefined) return
    const at = node.grid.cellToWorld({ ...e.anchor, gy: 0 })
    bus.emit('fx:play', { effect: 'poof', at: { x: at.x, y: at.y + 0.3, z: at.z }, count: 4 })
  })

  // A carried piece always goes home if anything interrupts.
  bus.on('app:modeChanged', () => cancelCarry())
  bus.on('scene:transitionStart', () => cancelCarry())
  bus.on('build:selected', () => cancelCarry())
  bus.on('build:toolChanged', () => cancelCarry())

  // Desktop convenience only. iPad is the primary platform; none of this is
  // required to play, and every one of these has a big button too.
  window.addEventListener('keydown', (ev) => {
    if (app.mode !== 'build') return
    const k = ev.key.toLowerCase()
    if (k === 'r') rotateSelection()
    else if (k === 'z') void undo()
    else if (k === 'escape') cancelCarry()
  })

  void ctx
}
